import GooglePayButton from '@google-pay/button-react';
import { useBehavior, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, TCommonDataObj, TWidgetProps } from '@libs/types';
import React, { Activity } from 'react';
import { TGooglePay } from '../../types/data';
import { GooglePayTokenizationType } from '../../types/enums';
import { TButtonOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './styles/payButtonWrapper.module.css';

export const ButtonGooglePay = ({ widget }: TWidgetProps<TGooglePay, TButtonOption>) => {
    const data = widget.data ?? {};

    const { isHidden } = useBehavior(widget);
    const { triggerActionsByNameAndType } = useTriggerAction(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification =
        data.tokenizationType === GooglePayTokenizationType.PAYMENT_GATEWAY
            ? ({
                  type: 'PAYMENT_GATEWAY',
                  parameters: {
                      gateway: data.gateway,
                      gatewayMerchantId: data.gatewayMerchantId,
                  },
              } as google.payments.api.PaymentGatewayTokenizationSpecification)
            : ({
                  type: 'DIRECT',
                  parameters: {
                      protocolVersion: data.protocolVersion,
                      publicKey: data.publicKey,
                  },
              } as google.payments.api.DirectTokenizationSpecification);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div data-test={widget.uid} className={styles.payButtonWrapper} style={getInlineStyles({ margin: widget.options?.margin })}>
                <GooglePayButton
                    style={{
                        width: '240px',
                        height: '56px',
                    }}
                    environment={data.environment === 'ENVIRONMENT_TEST' ? 'TEST' : 'PRODUCTION'}
                    buttonColor="default"
                    buttonType="plain"
                    buttonRadius={8}
                    buttonSizeMode="fill"
                    paymentRequest={{
                        apiVersion: 2,
                        apiVersionMinor: 0,
                        allowedPaymentMethods: [
                            {
                                type: 'CARD',
                                parameters: {
                                    allowedAuthMethods: data.supportedAuthMethods,
                                    allowedCardNetworks: data.supportedNetworks,
                                },
                                tokenizationSpecification: tokenizationSpecification,
                            },
                        ],
                        merchantInfo: {
                            merchantId: data.merchantId,
                            merchantName: data.merchantInfo?.merchantName,
                        },
                        shippingAddressParameters: {
                            allowedCountryCodes: data.allowedCountryCodes,
                        },
                        transactionInfo: {
                            totalPriceStatus: 'FINAL',
                            totalPriceLabel: 'Total',
                            totalPrice: data.value?.amount?.toString?.(),
                            currencyCode: data.value?.curIso,
                            countryCode: data.countryCode,
                        },
                    }}
                    onError={(error) => {
                        updateWidgetValue(error as unknown as TCommonDataObj);
                        triggerActionsByNameAndType(ActionActionTypeEnum.onClick, 'onFailure').catch((err) => console.log(err));
                    }}
                    onLoadPaymentData={(paymentRequest) => {
                        updateWidgetValue(paymentRequest as unknown as TCommonDataObj);
                        triggerActionsByNameAndType(ActionActionTypeEnum.onClick, 'onSuccess').catch((err) => console.log(err));
                    }}
                />
            </div>
        </Activity>
    );
};
